import { useAuth } from '@clerk/react';
import { useProfiler } from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  Outlet,
  useLocation,
  useRouter,
} from '@tanstack/react-router';
import { SpeedInsights } from '@vercel/speed-insights/react';
import cn from 'classnames';
import { DateTime } from 'luxon';
import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useToaster } from 'react-hot-toast/headless';
import { useTranslation } from 'react-i18next';

import type { PersonV1CurrentResponse } from 'client/types.gen';
import { Toaster } from 'components/banff-ui/Toast';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { GlobalTooltip } from 'components/UI/Tailwind/GlobalTooltip';
import { useAxiosAuthInterceptor } from 'helpers/auth';
import { captureOkr, setUser } from 'helpers/logging';
import { lazy } from 'helpers/react';
import { resolveCurrentUser } from 'helpers/session';
import { ErrorPage } from 'pages/Error';

// Automatically refresh page for users who may be leaving tabs open forever.
// The threshold value should be set high enough to avoid affecting majority
// of regular users.
const windowForceRefreshIntervalInSeconds =
  import.meta.env.VITE_APP_WINDOW_FORCE_REFRESH_INTERVAL_IN_SECONDS || 0;

const TanStackRouterDevtools =
  process.env.NODE_ENV === 'production'
    ? () => null
    : lazy(() =>
        import('@tanstack/react-router-devtools').then((response) => ({
          default: response.TanStackRouterDevtools,
        })),
      );

interface RouterContext {
  queryClient: QueryClient;
  user: PersonV1CurrentResponse | undefined;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // Only `user` is resolved here, for the guest guard and analytics. The role
  // is resolved together with the user in `/_private`, so children never pair
  // a user with a role from another load.
  beforeLoad: async ({ context }) => ({
    user: await resolveCurrentUser(context.queryClient),
  }),
  component: RootComponent,
});

function RootComponent() {
  useProfiler('App');
  const { t } = useTranslation();

  useAxiosAuthInterceptor();

  const auth = useAuth();
  const router = useRouter();
  const location = useLocation();
  const isInitialLoad = useRef(true);

  const { user } = Route.useRouteContext();

  const refSessionStart = useRef<DateTime>();

  const { handlers, toasts } = useToaster();
  const { calculateOffset, endPause, startPause, updateHeight } = handlers;

  useEffect(() => {
    if (windowForceRefreshIntervalInSeconds > 0) {
      refSessionStart.current = DateTime.fromISO(new Date().toISOString()).plus(
        { seconds: windowForceRefreshIntervalInSeconds },
      );
    }
  }, []);

  useEffect(() => {
    const threshold = refSessionStart.current;
    if (threshold && user?.id) {
      const now = DateTime.fromISO(new Date().toISOString());
      if (now > threshold) {
        captureOkr('refresh_due_to_stale_client', {
          timeout_seconds: windowForceRefreshIntervalInSeconds,
          user_id: user.id,
        });
        window.location.reload();
      }
    }
  }, [location.pathname, user?.id]);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    router.invalidate();
    // Only sign-in transitions should reload route context; `router` in deps caused
    // repeated invalidations in dev and contributed to navigation thrashing.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [auth.isSignedIn]);

  useEffect(() => {
    setUser(user);
  }, [user]);

  return (
    <ErrorBoundary
      fallback={<ErrorPage variant="fullScreen" />}
      name="RootComponent"
    >
      {/* Pages without their own title would otherwise keep the previous
          page's title. */}
      <Helmet defaultTitle={t('contact.company')} />
      <SpeedInsights
        debug={process.env.NODE_ENV !== 'production'}
        route={location.pathname}
        sampleRate={1.0}
      />
      <Outlet />
      <GlobalTooltip />
      <Toaster />
      <TanStackRouterDevtools />
      <div
        className="fixed bottom-2 right-2 z-20"
        onMouseEnter={startPause}
        onMouseLeave={endPause}
      >
        {toasts.map((x) => {
          const offset = calculateOffset(x, {
            gutter: 8,
            reverseOrder: false,
          });

          const ref = (el: HTMLDivElement | null) => {
            if (el && typeof x.height !== 'number') {
              const height = el.getBoundingClientRect().height;
              updateHeight(x.id, height);
            }
          };

          return (
            <div
              key={x.id}
              ref={ref}
              className={cn(
                'absolute bottom-0 right-0 z-20 min-w-max rounded-[30px] border border-solid border-gray-900 bg-gray-800 px-4 py-3 text-14/120% text-white transition-all duration-500 ease-out',
                {
                  'opacity-0': !x.visible,
                  'opacity-100': x.visible,
                },
              )}
              style={{
                transform: `translateY(-${offset}px)`,
              }}
              {...x.ariaProps}
            >
              {x.message as string}
            </div>
          );
        })}
      </div>
    </ErrorBoundary>
  );
}
